import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { formatPrice, formatMoney, isVideoUrl, isBundleProduct, sortedBundles } from "../lib/api";
import { ShoppingCart } from "lucide-react";
import QuickAddModal from "./QuickAddModal";

const ProductCard = ({ product, index = 0 }) => {
  const { lang, currency, t } = useApp();
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const name = product[`name_${lang}`] || product.name_en;
  const bundleMode = isBundleProduct(product);
  const packSizes = bundleMode ? sortedBundles(product).map((b) => b.min_qty).join("/") : "";

  return (
    <div
      className="group bg-white rounded-2xl p-4 transition-all duration-500 hover:shadow-card border border-transparent hover:border-border opacity-0 animate-fade-up"
      style={{ animationDelay: `${index * 60}ms` }}
      data-testid={`product-card-${product.slug}`}
    >
      <Link to={`/product/${product.slug}`}>
        <div className="bg-ivory rounded-xl aspect-[4/5] overflow-hidden mb-4 flex items-center justify-center relative">
          {product.images?.[0] && (
            isVideoUrl(product.images[0]) ? (
              <video
                src={product.images[0]}
                muted
                playsInline
                loop
                preload="metadata"
                onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <img
                src={product.images[0]}
                alt={name}
                className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
              />
            )
          )}
          {product.stock <= 0 && (
            <div className="absolute inset-0 bg-foreground/40 flex items-center justify-center">
              <span className="bg-ivory text-foreground text-xs px-3 py-1 rounded-full uppercase tracking-widest">{t("out_of_stock")}</span>
            </div>
          )}
        </div>
      </Link>
      <div className="px-1">
        <div className="text-[10px] tracking-[0.2em] uppercase text-holy-gold-dark font-semibold mb-1">
          {product.category}
        </div>
        <Link to={`/product/${product.slug}`} className="block">
          <h3 className="font-serif text-xl text-foreground leading-tight mb-2 hover:text-holy-red transition-colors">{name}</h3>
        </Link>
        <div className="flex items-center justify-between mt-3">
          {bundleMode ? (
            <div data-testid={`bundle-price-${product.slug}`}>
              <div className="text-holy-red font-semibold text-base font-sans">{formatPrice(product, currency)}</div>
              <div className="text-[10px] text-muted-foreground">/pc · {lang === "id" ? "paket" : "pack"} {packSizes} pcs</div>
            </div>
          ) : (
            <div className="text-holy-red font-semibold text-base font-sans">{formatPrice(product, currency)}</div>
          )}
          <button
            data-testid={`add-cart-${product.slug}`}
            onClick={() => setQuickAddOpen(true)}
            disabled={product.stock <= 0}
            aria-label={t("add_to_cart")}
            title={t("add_to_cart")}
            className="p-2.5 rounded-full bg-cream text-foreground hover:bg-holy-red hover:text-ivory hover:scale-110 transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            <ShoppingCart style={{ width: 18, height: 18 }} />
          </button>
        </div>
      </div>
      {quickAddOpen && <QuickAddModal product={product} onClose={() => setQuickAddOpen(false)} />}
    </div>
  );
};

export default ProductCard;
